'use client';
import React, { useState, useEffect } from 'react';
import { FileText, CheckCircle, AlertTriangle, PenLine, Tag, TrendingUp, Star, ArrowRight, Sparkles } from 'lucide-react';
import Link from 'next/link';
import type { OrderState } from './OrderWizardContent';
import { useAuth } from '@/contexts/AuthContext';
import {
  getUserMembership,
  calculateAdDiscount,
  MEMBERSHIP_DISCOUNTS,
  MEMBERSHIP_TIER_LABELS,
  type UserMembership,
} from '@/lib/membership';

interface Props {
  order: OrderState;
  updateOrder: (updates: Partial<OrderState>) => void;
  showErrors?: boolean;
}

const agreementItems = [
  {
    key: 'perRegion' as const,
    title: 'Per-Region Placement Acknowledgment',
    text: 'I understand that my ad placement applies independently to each selected region. Each region is a separate print run of 1,000 copies. My total charge reflects the number of regions selected multiplied by the per-region rate.',
  },
  {
    key: 'deadlines' as const,
    title: 'Artwork Deadline Agreement',
    text: 'I understand that artwork must be submitted by the stated deadline (7 days before print date). Late submissions may be deferred to the next available issue at no additional charge, but no refund will be issued for the current issue.',
  },
  {
    key: 'noRefund' as const,
    title: 'No Refunds / No Cancellations',
    text: 'I understand that all sales are final. Once payment is confirmed, no refunds or cancellations will be processed. For 3-issue agreements, I may cancel before the 2nd issue billing date to prevent future charges, but no refund is issued for completed issues.',
  },
  {
    key: 'chargeback' as const,
    title: 'Chargeback Policy',
    text: 'I agree not to initiate a chargeback for services rendered. In the event of a dispute, I will contact ZipAdBook directly at ads@zipadbook.com before initiating any dispute with my financial institution. Unauthorized chargebacks may result in account suspension and recovery action.',
  },
];

// Compute the base order total (same logic as OrderSummaryPanel)
function computeOrderTotal(order: OrderState): number {
  const baseRate = order.placementBasePrice ?? 0;
  const getDiscount = (count: number) => {
    if (order.placement !== 'standard') return 0;
    if (count >= 8) return 0.15;
    if (count >= 5) return 0.10;
    if (count >= 3) return 0.05;
    return 0;
  };
  const multiDiscount = getDiscount(order.regions.length);
  const discountedRate = Math.round(baseRate * (1 - multiDiscount));
  const subtotal = discountedRate * order.regions.length;
  const designFee = order.addDesign ? 150 : 0;
  return subtotal + designFee;
}

export default function StepF({ order, updateOrder, showErrors }: Props) {
  const { user } = useAuth();
  const [todayLabel, setTodayLabel] = useState('');
  const [todayValue, setTodayValue] = useState('');
  const [dateValue, setDateValue] = useState(order.signatureDate || '');
  const [membership, setMembership] = useState<UserMembership | null>(null);
  const [membershipLoading, setMembershipLoading] = useState(true);

  useEffect(() => {
    const now = new Date();
    setTodayLabel(now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
    const iso = now.toISOString().split('T')[0];
    setTodayValue(iso);
    if (!order.signatureDate) {
      setDateValue(iso);
      updateOrder({ signatureDate: iso });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setMembership({ tier: null, status: 'inactive', startDate: null, endDate: null, isActive: false });
      setMembershipLoading(false);
      return;
    }
    getUserMembership(user.id).then((m) => {
      setMembership(m);
      setMembershipLoading(false);
    });
  }, [user?.id]);

  const allChecked = Object.values(order.agreementChecks).every(Boolean);
  const hasSig = order.signatureName.trim().length > 2;
  const missingChecks = showErrors && !allChecked;
  const missingSignature = showErrors && allChecked && !hasSig;

  const toggleCheck = (key: keyof typeof order.agreementChecks) => {
    updateOrder({
      agreementChecks: {
        ...order.agreementChecks,
        [key]: !order.agreementChecks[key],
      },
    });
  };

  // Savings calculation
  const orderTotal = computeOrderTotal(order);
  const savingsData = membership ? calculateAdDiscount(membership, orderTotal) : null;
  const hasMemberDiscount = savingsData && savingsData.discountAmount > 0;
  const isNonMember = !membership?.isActive;
  const isBusinessTier = membership?.tier === 'business' && membership?.isActive;

  // What they'd save at each tier (for upsell)
  const businessSavings = Math.round(orderTotal * ((MEMBERSHIP_DISCOUNTS.business.min + MEMBERSHIP_DISCOUNTS.business.max) / 2));
  const premiumSavings = Math.round(orderTotal * ((MEMBERSHIP_DISCOUNTS.premium.min + MEMBERSHIP_DISCOUNTS.premium.max) / 2));

  return (
    <div className="animate-slide-up">
      {/* ── Estimated Savings Breakdown ── */}
      {!membershipLoading && orderTotal > 0 && (
        <div className="mb-6">
          {hasMemberDiscount && savingsData ? (
            /* Active member with discount */
            <div className="rounded-2xl border border-green-500/30 bg-green-500/8 p-5">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Tag size={15} className="text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-green-400">Member Savings Applied</p>
                  <p className="text-xs text-slate-400">{MEMBERSHIP_TIER_LABELS[membership!.tier!]}</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="font-display font-bold text-xl text-green-400 tabular-nums">
                    -${savingsData.discountAmount.toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-400">saved on this order</p>
                </div>
              </div>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between text-slate-400">
                  <span>Order subtotal</span>
                  <span className="tabular-nums line-through text-slate-500">${orderTotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-400">{savingsData.discountLabel}</span>
                  <span className="text-green-400 tabular-nums">-${savingsData.discountAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-semibold pt-1 border-t border-green-500/20">
                  <span className="text-white">Your price</span>
                  <span className="text-white tabular-nums">${savingsData.finalPrice.toLocaleString()}</span>
                </div>
              </div>
              {/* Upgrade upsell for business → premium */}
              {isBusinessTier && (
                <div className="mt-4 pt-3 border-t border-green-500/20 flex items-start gap-2.5">
                  <Sparkles size={13} className="text-amber-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-amber-400">Upgrade to Premium Growth</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Premium members save {MEMBERSHIP_DISCOUNTS.premium.label} — you'd save{' '}
                      <span className="text-amber-400 font-semibold">${premiumSavings.toLocaleString()}</span> on this order instead of ${savingsData.discountAmount.toLocaleString()}.
                    </p>
                  </div>
                  <Link
                    href="/advertiser-dashboard"
                    className="shrink-0 flex items-center gap-1 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors"
                  >
                    Upgrade <ArrowRight size={11} />
                  </Link>
                </div>
              )}
            </div>
          ) : isNonMember ? (
            /* Non-member upsell banner */
            <div className="rounded-2xl border border-brand-500/30 bg-brand-500/8 p-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-primary flex items-center justify-center shrink-0">
                  <Star size={16} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white mb-0.5">Unlock Member Savings on This Order</p>
                  <p className="text-xs text-slate-400 leading-relaxed mb-3">
                    Members save on every ad placement. Join before paying and the discount applies immediately.
                  </p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="rounded-xl bg-navy-700/60 border border-white/8 p-3">
                      <p className="text-xs font-semibold text-white mb-0.5">Business Member</p>
                      <p className="text-xs text-slate-400">{MEMBERSHIP_DISCOUNTS.business.label} off ads</p>
                      <p className="text-brand-400 font-semibold text-sm tabular-nums mt-1">
                        Save ~${businessSavings.toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-xl bg-amber-500/10 border border-amber-500/25 p-3">
                      <p className="text-xs font-semibold text-amber-400 mb-0.5">Premium Growth</p>
                      <p className="text-xs text-slate-400">{MEMBERSHIP_DISCOUNTS.premium.label} off ads</p>
                      <p className="text-amber-400 font-semibold text-sm tabular-nums mt-1">
                        Save ~${premiumSavings.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <Link
                    href="/advertiser-dashboard"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-400 hover:text-brand-300 transition-colors"
                  >
                    <TrendingUp size={12} />
                    View membership plans
                    <ArrowRight size={11} />
                  </Link>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      <div className="mb-6">
        <h2 className="font-display text-2xl font-700 text-white mb-1">Advertising Agreement</h2>
        <p className="text-sm text-slate-400">
          Review and accept the key terms before proceeding to payment. Your electronic signature constitutes a binding agreement.
        </p>
      </div>

      {/* Agreement header */}
      <div className="glass-card rounded-2xl p-5 border border-brand-600/20 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center">
            <FileText size={18} className="text-white" />
          </div>
          <div>
            <p className="font-semibold text-white">ZipAdBook Advertising Agreement</p>
            <p className="text-xs text-slate-400">Issue #14 · {todayLabel || 'Loading…'}</p>
          </div>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed">
          This agreement is between the advertiser (you) and ZipAdBook, a publication of Take a Zip LLC. By checking the boxes below and providing your electronic signature, you agree to the following terms in full.
        </p>
      </div>

      {/* Agreement items */}
      <div className="space-y-3 mb-6">
        {agreementItems.map((item) => {
          const checked = order.agreementChecks[item.key];
          return (
            <button
              key={`agreement-${item.key}`}
              type="button"
              onClick={() => toggleCheck(item.key)}
              className={`w-full text-left p-5 rounded-xl border transition-all duration-200 ${
                checked
                  ? 'border-green-500/40 bg-green-500/8'
                  : missingChecks
                  ? 'border-red-500/35 bg-navy-700/40 hover:border-brand-500/25' :'border-white/10 bg-navy-700/40 hover:border-brand-500/25'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
                  checked ? 'bg-green-500 border-green-500' : 'border-white/25 bg-transparent'
                }`}>
                  {checked && <CheckCircle size={13} className="text-white" />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-white mb-1">{item.title}</p>
                  <p className="text-xs text-slate-400 leading-relaxed">{item.text}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {missingChecks && (
        <p className="mb-4 flex items-center gap-1.5 text-xs text-red-400">
          <AlertTriangle size={12} />
          All four agreement boxes must be checked before continuing.
        </p>
      )}

      {/* E-signature */}
      <div className={`glass-card rounded-2xl p-6 border transition-all ${
        allChecked ? 'border-brand-600/30' : 'border-white/8 opacity-60 pointer-events-none'
      }`}>
        <div className="flex items-center gap-2 mb-4">
          <PenLine size={16} className="text-brand-400" />
          <h3 className="font-semibold text-white text-sm">Electronic Signature</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Full Legal Name
              <span className="text-red-400 ml-1">*</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">Type your full name as it appears on your business registration</p>
            <input
              type="text"
              value={order.signatureName}
              onChange={(e) => updateOrder({ signatureName: e.target.value })}
              className={`w-full px-4 py-3 bg-navy-700 border rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition-colors font-medium ${missingSignature ? 'border-red-500/60' : 'border-white/12'}`}
              placeholder="Maria Torres"
            />
            {missingSignature && (
              <p className="mt-1 flex items-center gap-1 text-xs text-red-400">
                <AlertTriangle size={12} />
                Full legal name is required (minimum 3 characters).
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Date</label>
            <p className="text-xs text-slate-500 mb-2">Today's date</p>
            <input
              type="date"
              value={dateValue}
              max={todayValue}
              onChange={(e) => {
                setDateValue(e.target.value);
                updateOrder({ signatureDate: e.target.value });
              }}
              className="w-full px-4 py-3 bg-navy-700 border border-white/12 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition-colors"
            />
          </div>
        </div>

        {/* Signature preview */}
        {hasSig && (
          <div className="mt-4 p-4 rounded-xl bg-navy-800 border border-white/8">
            <p className="text-xs text-slate-500 mb-2">Signature preview:</p>
            <p className="font-display text-xl text-white" style={{ fontStyle: 'italic' }}>
              {order.signatureName}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Signed electronically · {dateValue || 'Date not set'} · IP recorded at submission
            </p>
          </div>
        )}
      </div>

      {!allChecked && (
        <div className="mt-4 flex items-center gap-2.5 p-3 rounded-xl bg-amber-500/8 border border-amber-500/20">
          <AlertTriangle size={14} className="text-amber-400 shrink-0" />
          <p className="text-xs text-slate-400">Check all four agreement boxes to unlock the signature field.</p>
        </div>
      )}
    </div>
  );
}